import { Consideration } from './Consideration';
import {
  ParsedSupplement,
  parseSupplementItem,
} from '../utils/supplementItem';

export class FurtherConsiderations {
  considerations: Consideration[] = [];

  static createFromConsiderations(considerations: Consideration[]) {
    const furtherConsiderations = new FurtherConsiderations();

    if (considerations) {
      furtherConsiderations.considerations = considerations.map(
        consideration => {
          return new Consideration(consideration);
        },
      );
    } else {
      furtherConsiderations.considerations = [];
    }

    return furtherConsiderations;
  }

  constructor(furtherConsiderations?: FurtherConsiderations) {
    if (!furtherConsiderations) {
      return;
    }

    Object.keys(furtherConsiderations).forEach(key => {
      this[key] = furtherConsiderations[key];
    });

    if (furtherConsiderations.considerations) {
      this.considerations = furtherConsiderations.considerations.map(
        consideration => {
          return new Consideration(consideration);
        },
      );
    } else {
      this.considerations = [];
    }
  }

  findConsiderations(
    categoryId: number,
    section?: string,
    tab?: string,
    brands?: string[],
  ): Consideration[] {
    return (this.considerations || []).filter(consideration => {
      const isBrandMatch = !brands || brands.includes(consideration.brand);

      if (section && tab) {
        return (
          consideration.categoryId === categoryId &&
          consideration.section === section &&
          consideration.tab === tab &&
          isBrandMatch
        );
      }

      return consideration.categoryId === categoryId && isBrandMatch;
    });
  }

  findConsiderationsByCategoryAndSubCategory(
    categoryId: number,
    subCategoryId: number,
  ): Consideration[] {
    return (this.considerations || []).filter(consideration => {
      return (
        consideration.categoryId === categoryId &&
        consideration.subCategoryId === subCategoryId
      );
    });
  }

  // Resolve a Reference Library slug (e.g. "ada_diagnosis_classification_diabetes_2026")
  // to its Consideration row. Tags are unique across the table — the sheet's
  // Reference Library tab enforces it — so the first match wins.
  findByTag(tag: string): Consideration | undefined {
    if (!tag) return undefined;
    return (this.considerations || []).find(c => c.tag === tag);
  }

  // Resolve a Supplements bullet to its display parts. A bullet is either a
  // supplement-library tag (a cat 500xxx row — the de-duplicated form, items =
  // [description, link]) or a legacy inline "Name — Description — url" string.
  // findByTag is tried FIRST: supplement tags can contain spaces/hyphens (e.g.
  // "ga - adrenal", "cvo-r") that parseSupplementItem would mis-split on its
  // " - " separator, and a legacy string never matches a tag. Returns null for
  // unstructured lines (e.g. the "Use code …" promo) so callers render them as
  // plain text. Mirrors the Reference Library's findByTag resolution.
  resolveSupplement(item: string): ParsedSupplement | null {
    const tag = (item || '').trim();
    const row = this.findByTag(tag);
    if (row) {
      const [description = '', link = ''] = row.items || [];
      return { name: row.title, description, link: link || undefined };
    }
    return parseSupplementItem(item);
  }
}